<template>
    <component :is="indicator ? 'span' : 'label'" :for="indicator ? undefined : id" class="flex flex-row gap-4 items-center">
        <!-- As an indicator the box sits inside a control that owns the state (a selectable card
             button), so it renders no label and no focusable element of its own. -->
        <CheckboxRoot
            :id="id"
            v-slot="{ state }"
            :as="indicator ? 'span' : 'button'"
            :aria-hidden="indicator ? 'true' : undefined"
            :aria-labelledby="fieldAttrs['aria-labelledby']"
            :aria-describedby="fieldAttrs['aria-describedby']"
            :aria-invalid="fieldAttrs['aria-invalid']"
            v-bind="$attrs"
            :data-tone="tone"
            :class="[shape === 'circle' ? 'rounded-full' : 'rounded', indicator && 'pointer-events-none']"
            class="flex h-5 min-w-5 max-w-5 appearance-none items-center justify-center overflow-hidden bg-control-bg border border-control-border-strong aria-invalid:border-status-error aria-invalid:ring-[3px] aria-invalid:ring-status-error-soft"
            @update:model-value="$emit('update:modelValue', $event)">
            <CheckboxIndicator class="bg-control-bg h-full w-full flex items-center justify-center">
                <!-- Both glyph sets render because the tonal flag is DOM state (html[data-theme]) that
                     JS cannot read; the style block below picks the one for the active theme. -->
                <i class="bi text-base/none legacy-glyph" :class="[accentClass, legacyIconClass(state === 'indeterminate')]" aria-hidden="true"></i>
                <i class="bi text-base/none tonal-glyph" :class="[accentClass, state === 'indeterminate' ? 'bi-dash-lg' : 'bi-check-lg']" aria-hidden="true"></i>
            </CheckboxIndicator>
        </CheckboxRoot>
        <span v-if="$slots.default" :id="textId" class="select-none">
            <slot></slot>
        </span>
    </component>
</template>

<script setup lang="ts">
import { CheckboxIndicator, CheckboxRoot, type CheckboxRootEmits } from "reka-ui";
import { computed, useId, useSlots } from "vue";

import { useCheckboxGroup } from "./checkboxGroupContext";
import { useFormField } from "./formFieldContext";

type BaseCheckboxProps = {
    /** Id for a checkbox used on its own or in a group. Inside a FormField the field's id wins, so its label keeps toggling the box. */
    checkboxId?: string;
    isFilled?: boolean;
    shape?: "circle" | "square";
    /** `success` paints the checked state with the success status color, for completion lists. */
    tone?: "accent" | "success";
    /**
     * Draws the box as a non-interactive indicator for a parent control that carries the state
     * itself, such as a selectable `BaseCard as="button"` with `aria-pressed`. It renders no label,
     * takes no focus or clicks and is hidden from assistive tech, so the parent stays the one control.
     */
    indicator?: boolean;
};

const { isFilled = true, shape = "circle", tone = "accent", checkboxId, indicator = false } = defineProps<BaseCheckboxProps>();

const accentClass = computed(() => (tone === "success" ? "text-status-success" : "text-control-accent"));

// Inside a FormField the checkbox takes the field's id, hint/error description and invalid state,
// and is named by the field label followed by its own inline label. Two `<label for>` elements
// would also name it, but screen readers disagree on whether they read both, so aria-labelledby
// spells the order out. Inside a BaseCheckboxGroup the group carries the field wiring instead:
// each checkbox keeps its own id and inline label and only mirrors the invalid state. Used bare,
// the inline label is the only name. In every case we point aria-labelledby at our own inline
// label span rather than leaving CheckboxRoot to fall back to reading the `[for]` label's
// innerText: that fallback is a one-time DOM read taken at mount (reka-ui's CheckboxRoot,
// `ariaLabel` computed keyed only on `props.id`/`currentElement`), so a checkbox that mounts
// before vue-i18n's locale catalogue loads keeps an English accessible name forever, even once
// the visible label re-renders translated. aria-labelledby is read live by assistive tech, so it
// stays correct across a later locale switch. Attributes passed by the caller (aria-labelledby,
// aria-describedby, ...) win.
const field = useFormField();
const group = useCheckboxGroup();
const slots = useSlots();
const fallbackId = useId();
const textId = `${fallbackId}-text`;

const inField = computed(() => !!field && !group);
// Inside a FormField the field's id wins, so the field label's `for` keeps naming and toggling the box.
const id = computed(() => (inField.value ? field?.controlId : checkboxId) ?? checkboxId ?? fallbackId);
const fieldAttrs = computed(() => {
    const labelledBy = [inField.value ? field?.labelId : undefined, slots.default ? textId : undefined].filter(Boolean).join(" ");

    const describedBy = inField.value ? field?.describedBy.value : group?.describedBy.value;

    const invalid = (group ?? field)?.invalid.value;

    return {
        "aria-labelledby": labelledBy || undefined,
        "aria-describedby": describedBy,
        "aria-invalid": invalid ? ("true" as const) : undefined,
    };
});

defineEmits<CheckboxRootEmits>();

function legacyIconClass(indeterminate: boolean) {
    let iconName = indeterminate ? "bi-dash" : "bi-check";
    iconName += shape === "circle" ? "-circle" : "-square";
    return isFilled ? `${iconName}-fill` : iconName;
}
</script>

<style scoped>
@reference '../../../../css/tailwind.css';
/* Tonal glyphs per the design-system source's 27 Aug changelog; gated in CSS
   so the flag-off render keeps today's icons exactly. */
.tonal-glyph {
    display: none;
}

html[data-theme="tonal"] .legacy-glyph {
    display: none;
}

html[data-theme="tonal"] .tonal-glyph {
    /* 12px tick centered in the 20px box; the element's text-base/none would
       render a 16px glyph that overflows and sits off-center on its baseline.
       The translate is optical centering: the bi-check-lg path sits ~0.6px
       above its em-box center, and its rising right arm makes a
       geometrically-centered tick read right-heavy, so it gets a 0.5px left
       bias on top. */
    @apply inline text-sm/none -translate-x-[0.5px] translate-y-[0.6px];
}

/* The spec's checked state is a filled box with a white tick, not an outline
   with an ink tick: box fills with the control accent (neutral-800 under the
   flag) when checked or indeterminate, and the plain-stroke glyphs read white
   on it. Flag off is untouched: the -fill glyph family paints its own box. */
html[data-theme="tonal"] [data-state="checked"],
html[data-theme="tonal"] [data-state="indeterminate"] {
    background-color: var(--color-control-accent);
    border-color: var(--color-control-accent);
}

html[data-theme="tonal"] [data-tone="success"][data-state="checked"] {
    background-color: var(--color-status-success);
    border-color: var(--color-status-success);
}

html[data-theme="tonal"] [data-state="checked"] :deep(.bg-control-bg),
html[data-theme="tonal"] [data-state="indeterminate"] :deep(.bg-control-bg) {
    background-color: transparent;
}

html[data-theme="tonal"] [data-state="checked"] .tonal-glyph,
html[data-theme="tonal"] [data-state="indeterminate"] .tonal-glyph {
    color: #ffffff;
}
</style>
